/**
 * AI CONTEXT
 * Purpose: hoverable quick-note row with complete/delete controls and relative-time metadata.
 * Depends on: user lookup for author and withHoverable wrapper.
 * Used by: QuickNotes list sections.
 * Refactor: move time-refresh interval to shared relative-time hook.
 */
import { CheckCircleTwoTone, DeleteOutlined } from "@ant-design/icons"
import { Button } from "antd"
import dayjs from "dayjs"
import { motion } from "motion/react"
import { useEffect, useState } from "react"

import FormattedTextBlock from "#blocks/FormattedTextBlock"
import withHoverable from "#blocks/withHoverable"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { QuickNotes } from "#customTypes/composedTypes/quickNotesTypes"
import { useGetOneUser } from "#hooks/newHooks/useGetUser"
import { getNow } from "#utils/dateHelpers"
import useIsSuperView from "#views/SuperView/useIsSuperView"

type QuickNoteListItemProps = {
  item: QuickNotes
  isHovered: boolean
  onDelete: (id: string) => void
  onComplete: (id: string) => void
}

const QuickNoteListItemBase = ({
  item,
  isHovered,
  onDelete,
  onComplete,
}: QuickNoteListItemProps) => {
  const isSuperView = useIsSuperView()
  const theme = useThemeContext()
  const author = useGetOneUser(item.createdBy)

  const [fuzzyTime, setFuzzyTime] = useState(() =>
    dayjs(item.createdAt).from(getNow())
  )

  useEffect(() => {
    const interval = setInterval(() => {
      setFuzzyTime(dayjs(item.createdAt).from(getNow()))
    }, 60000)

    return () => clearInterval(interval)
  }, [item.createdAt])

  const isCompleted = item.completedAt != null

  const textStyle = isSuperView
    ? {
        fontSize: 12,
        padding: "3px 8px",
      }
    : {}

  const subtitleStyle = isSuperView
    ? { fontSize: 10, padding: "0 8px" }
    : { fontSize: 11, padding: "0 8px" }

  return (
    <motion.div
      layoutId={item.id}
      id={item.id}
      style={{
        border: "1px solid #aaa",
        opacity: isCompleted ? 0.5 : 1,
        borderRadius: 9,
        width: isSuperView ? 370 : 270,
        flexShrink: 0,
        padding: "2px",
        position: "relative",
        backgroundColor: theme.white,
      }}
    >
      {isHovered && (
        <motion.div
          style={{ position: "absolute", top: 4 }}
          initial={{ opacity: 0, right: 0 }}
          animate={{ opacity: 1, right: 4 }}
          exit={{ opacity: 0, right: 0 }}
          transition={{ duration: 0.3 }}
        >
          {isCompleted ? (
            <Button
              icon={<DeleteOutlined />}
              size="small"
              type="text"
              danger
              shape="circle"
              onClick={() => onDelete(item.id)}
            />
          ) : (
            <Button
              icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
              size="small"
              type="text"
              shape="circle"
              onClick={() => onComplete(item.id)}
            />
          )}
        </motion.div>
      )}
      <FormattedTextBlock text={item.description} style={textStyle} />
      <div style={{ ...subtitleStyle, color: theme.gray[7] }}>
        {author ? `— ${author.firstName}, ` : ""}
        {fuzzyTime}
      </div>
    </motion.div>
  )
}

const QuickNoteListItem = withHoverable<QuickNoteListItemProps>(
  QuickNoteListItemBase
)

export default QuickNoteListItem
